import { useState, useCallback } from 'react';
import api from '../api/client';

/**
 * useUpdateOrderStatus
 * Универсальный хук для редактирования статуса/количества заказов.
 * Тип материала выбирается в попапе и передаётся в запросы.
 *
 * @param {Function} onSuccess — callback после успешного сохранения
 */
export const useUpdateOrderStatus = ({ onSuccess }) => {
  const [materialtypeID, setMaterialtypeID] = useState('');
  const [orderID, setOrderID]               = useState('');
  const [year, setYear]                     = useState('');
  const [monthNum, setMonthNum]             = useState('');
  const [orderRows, setOrderRows]           = useState([]);
  const [editedRows, setEditedRows]         = useState([]);
  const [orderIDs, setOrderIDs]             = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [submitResult, setSubmitResult]     = useState(null);
  const [error, setError]                   = useState('');

  // При смене типа — сбрасываем результаты поиска
  const handleMaterialTypeChange = useCallback((id) => {
    setMaterialtypeID(id);
    setOrderIDs([]);
    setOrderRows([]);
    setEditedRows([]);
    setError('');
    setSubmitResult(null);
    setOrderID('');
  }, []);

  const fetchOrderIDs = useCallback(async (typeID) => {
    const tid = typeID ?? materialtypeID;
    if (!tid) return;
    try {
      const res = await api.get('/api/order_ids_by_type', {
        params: { materialtypeID: tid }
      });
      setOrderIDs(res.data);
    } catch (e) {
      console.error('Failed to fetch order IDs', e);
    }
  }, [materialtypeID]);

  const fetchOrderRows = useCallback(async (oid, yr, mn) => {
    const id  = oid ?? orderID;
    const tid = materialtypeID;

    if (!tid) { setError('Please select a material type'); return; }
    if (!id?.trim()) { setError('Please enter an Order ID'); return; }

    setIsLoading(true);
    setError('');
    setSubmitResult(null);

    try {
      const params = {
        orderID: id.trim(),
        materialtypeID: tid,
      };
      if (yr ?? year) params.year = yr ?? year;
      if (mn ?? monthNum) params.month_num = mn ?? monthNum;

      const res = await api.get('/api/orders_by_orderid', { params });

      if (res.data.length === 0) {
        setError('No orders found for this Order ID and material type');
        setOrderRows([]);
        setEditedRows([]);
      } else {
        setOrderRows(res.data);
        setEditedRows(res.data.map(r => ({
          ...r,
          _statusID:     r.statusID,
          _qty:          r.qty,
          _prevStatusID: r.statusID,
        })));
      }
    } catch (e) {
      setError('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  }, [materialtypeID, orderID, year, monthNum]);

  const updateRow = useCallback((index, field, value) => {
    setEditedRows(prev => prev.map((r, i) =>
      i === index ? { ...r, [field]: value } : r
    ));
  }, []);

  // Строки, уже сохранённые как D (получены), не трогаем — смена статуса
  // с D откатывает сток; для этого есть построчный селект.
  const applyStatusToAll = useCallback((statusID) => {
    setEditedRows(prev => prev.map(r => (r.statusID === 'D' ? r : { ...r, _statusID: statusID })));
  }, []);

  const submitChanges = useCallback(async () => {
    const changed = editedRows.filter(
      (r, i) => r._statusID !== orderRows[i].statusID || parseInt(r._qty) !== parseInt(orderRows[i].qty)
    );

    if (changed.length === 0) {
      setSubmitResult({ success: false, message: 'No changes to save' });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const payload = changed.map(r => ({
        sku:           r.sku,
        year:          r.year,
        month_num:     r.month_num,
        orderID:       r.orderID,
        statusID:      r._statusID,
        qty:           parseInt(r._qty),
        prevStatusID:  r._prevStatusID,
        materialtypeID: parseInt(materialtypeID),
      }));

      await api.put('/api/update_order_rows', { rows: payload });

      setSubmitResult({ success: true, message: `Updated ${changed.length} row(s) successfully.` });
      await fetchOrderRows(orderID, year, monthNum);
      if (onSuccess) onSuccess();
    } catch (e) {
      setSubmitResult({ success: false, message: e.response?.data?.error || 'Failed to update orders' });
    } finally {
      setIsSubmitting(false);
    }
  }, [editedRows, orderRows, materialtypeID, orderID, year, monthNum, fetchOrderRows, onSuccess]);

  const deleteRow = useCallback(async (sku, rowYear, rowMonth) => {
    await api.delete('/api/material-orders/delete-item', {
      data: {
        isSeeds: false,
        sku,
        year:          parseInt(rowYear),
        month_num:     parseInt(rowMonth),
        materialtypeID: parseInt(materialtypeID),
      },
    });
    await fetchOrderRows();
  }, [materialtypeID, fetchOrderRows]);

  const reset = useCallback(() => {
    setOrderID('');
    setYear('');
    setMonthNum('');
    setOrderRows([]);
    setEditedRows([]);
    setError('');
    setSubmitResult(null);
    // materialtypeID не сбрасываем
  }, []);

  const hasChanges = editedRows.some(
    (r, i) => r._statusID !== orderRows[i]?.statusID || parseInt(r._qty) !== parseInt(orderRows[i]?.qty)
  );

  return {
    materialtypeID, handleMaterialTypeChange,
    orderID, setOrderID,
    year, setYear,
    monthNum, setMonthNum,
    orderIDs,
    orderRows,
    editedRows,
    isLoading,
    isSubmitting,
    submitResult,
    error,
    hasChanges,
    fetchOrderIDs,
    fetchOrderRows,
    updateRow,
    applyStatusToAll,
    submitChanges,
    deleteRow,
    reset,
  };
};